/** SocialResultCard — the /social command's "Your social presence" card. */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';

interface SocialHighlight {
  title: string;
  description: string;
  url: string;
  age?: string;
}

interface XProfile {
  url: string;
  handle: string;
  followers?: string;
  highlights: SocialHighlight[];
}

interface LinkedInProfile {
  url: string;
  headline: string;
}

interface Mention {
  title: string;
  url: string;
  description: string;
  age?: string;
}

interface SocialReport {
  name: string;
  bio?: string | null;
  xProfile: XProfile | null;
  linkedIn: LinkedInProfile | null;
  recentMentions: Mention[];
}

function hostname(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

const DOMAIN_BADGES: Record<string, string> = {
  'goodreads.com': 'Book',
  'amazon.com': 'Book',
};

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17L17 7M17 7H7M17 7v10"/>
    </svg>
  );
}

export function SocialResultCard({ initialData }: { initialData?: SocialReport | null } = {}) {
  const [report, setReport] = useState<SocialReport | null | undefined>(
    initialData !== undefined ? initialData : undefined,
  );
  const { token } = useSession();

  useEffect(() => {
    if (initialData !== undefined) return; // already seeded
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const data = await apiFetch<{ socialReport: SocialReport | null }>(
          `/workspaces/${wsId}/social-report`,
          { authToken: token },
        );
        if (alive) setReport(data?.socialReport ?? null);
      } catch {
        if (alive) setReport(null);
      }
    })();
    return () => { alive = false; };
  }, [token, initialData]);

  if (report === undefined) return null;
  if (!report) return <p style={{ fontSize: 13, color: 'var(--color-text-quiet)' }}>No social data found — check your name is set in account settings.</p>;

  const { xProfile, linkedIn, recentMentions, bio } = report;
  const profileCount = (xProfile ? 1 : 0) + (linkedIn ? 1 : 0);

  return (
    <div style={{ background: 'var(--color-background-secondary)', borderRadius: 'var(--border-radius-lg)', padding: 4 }}>
      <div style={{ background: 'var(--color-surface-raised, #FAFAF7)', border: '0.5px solid var(--color-border, #E4E2D8)', borderRadius: 14, padding: '24px 26px', fontFamily: 'inherit', color: 'var(--color-text-primary)' }}>

        <div style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 500, color: 'var(--color-text-quiet)', textTransform: 'uppercase' }}>Social</div>
        <div style={{ fontSize: 24, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.01em' }}>{report.name}</div>

        {bio && (
          <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--color-text-secondary)', margin: '10px 0 0', maxWidth: '92%' }}>{bio}</p>
        )}

        <div style={{ display: 'flex', margin: '20px 0 4px', borderTop: '0.5px solid var(--color-border, #E4E2D8)', borderBottom: '0.5px solid var(--color-border, #E4E2D8)' }}>
          {xProfile?.followers && (
            <div style={{ flex: 1, padding: '14px 0' }}>
              <div style={{ fontSize: 20, fontWeight: 500 }}>{xProfile.followers}</div>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--color-text-quiet)', textTransform: 'uppercase', marginTop: 3 }}>Followers</div>
            </div>
          )}
          {profileCount > 0 && (
            <div style={{ flex: 1, padding: '14px 0', ...(xProfile?.followers ? { paddingLeft: 20, borderLeft: '0.5px solid var(--color-border, #E4E2D8)' } : {}) }}>
              <div style={{ fontSize: 20, fontWeight: 500 }}>{profileCount}</div>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--color-text-quiet)', textTransform: 'uppercase', marginTop: 3 }}>Profiles</div>
            </div>
          )}
          {recentMentions.length > 0 && (
            <div style={{ flex: 1, padding: '14px 0', paddingLeft: 20, borderLeft: '0.5px solid var(--color-border, #E4E2D8)' }}>
              <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--color-brand, #15A5E3)' }}>{recentMentions.length}</div>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--color-text-quiet)', textTransform: 'uppercase', marginTop: 3 }}>Mentions · 90d</div>
            </div>
          )}
        </div>

        {(xProfile || linkedIn) && (
          <>
            <div style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 500, color: 'var(--color-text-quiet)', textTransform: 'uppercase', marginTop: 22 }}>Profiles</div>

            {xProfile && (
              <a
                href={xProfile.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0', borderBottom: linkedIn ? '0.5px solid var(--color-border-subtle, #EDEBE2)' : 'none', textDecoration: 'none', color: 'inherit' }}
              >
                <span style={{ marginTop: 2, color: 'var(--color-text-primary)' }}><XIcon /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{xProfile.handle}</div>
                  {xProfile.highlights[0] && (
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {xProfile.highlights[0].description.slice(0, 60)}{xProfile.highlights[0].description.length > 60 ? '…' : ''}
                    </div>
                  )}
                </div>
                <span style={{ marginTop: 2, color: 'var(--color-brand, #15A5E3)', flexShrink: 0 }}><ExternalLinkIcon /></span>
              </a>
            )}

            {linkedIn && (
              <a
                href={linkedIn.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0', textDecoration: 'none', color: 'inherit' }}
              >
                <span style={{ marginTop: 2, color: 'var(--color-text-primary)' }}><LinkedInIcon /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{linkedIn.url.replace('https://www.', '').replace('https://', '').replace('linkedin.com/in/', '/in/')}</div>
                  {linkedIn.headline && (
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {linkedIn.headline.slice(0, 70)}{linkedIn.headline.length > 70 ? '…' : ''}
                    </div>
                  )}
                </div>
                <span style={{ marginTop: 2, color: 'var(--color-brand, #15A5E3)', flexShrink: 0 }}><ExternalLinkIcon /></span>
              </a>
            )}
          </>
        )}

        {recentMentions.length > 0 && (
          <>
            <div style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 500, color: 'var(--color-text-quiet)', textTransform: 'uppercase', marginTop: 22, borderTop: '0.5px solid var(--color-border, #E4E2D8)', paddingTop: 20 }}>
              Mentions <span style={{ color: 'var(--color-border-strong, #c4c2b6)' }}>·</span> <span style={{ color: 'var(--color-text-tertiary, #b3b1a5)' }}>{recentMentions.length}</span>
            </div>
            {recentMentions.map((m, i) => {
              const domain = hostname(m.url);
              const badge = DOMAIN_BADGES[domain];
              return (
                <a
                  key={i}
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', gap: 16, padding: '13px 0', borderBottom: i < recentMentions.length - 1 ? '0.5px solid var(--color-border-subtle, #EDEBE2)' : 'none', textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{ flex: 1, fontSize: 14, lineHeight: 1.4, color: 'var(--color-text-primary)' }}>
                    {m.title}
                    {badge && (
                      <span style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--color-text-quiet)', border: '0.5px solid var(--color-border, #D8D6CB)', padding: '1px 6px', marginLeft: 8, textTransform: 'uppercase', verticalAlign: '1px' }}>{badge}</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
                    <div style={{ fontSize: 12, color: 'var(--color-text-quiet)' }}>{domain}</div>
                    {m.age && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #b3b1a5)', marginTop: 2 }}>{m.age}</div>}
                  </div>
                </a>
              );
            })}
          </>
        )}

      </div>
    </div>
  );
}
